import type { SettingsComponentKey } from '@/config/settingsRegistry';
import { getSettingsMasterByPath, SETTINGS_MASTER_BY_ROUTE } from '@/config/settingsRegistry';

const prefetched = new Set<string>();

/** Direct chunk imports for settings masters (warm before click). */
const COMPONENT_IMPORTS: Partial<Record<SettingsComponentKey, () => Promise<unknown>>> = {
  organizationProfile: () => import('@/pages/org/OrganizationProfilePage'),
  companies: () => import('@/pages/org/CompaniesPage'),
  employees: () => import('@/pages/org/EmployeesPage'),
  roles: () => import('@/pages/org/RolesPage'),
  stores: () => import('@/pages/org/StoresPage'),
  products: () => import('@/pages/org/ProductsPage'),
  categories: () => import('@/pages/org/CategoriesPage'),
  brands: () => import('@/pages/org/BrandsPage'),
  units: () => import('@/pages/org/UnitsPage'),
  taxRules: () => import('@/pages/org/TaxRulesPage'),
  storeProducts: () => import('@/pages/org/StoreProductsPage'),
  discounts: () => import('@/pages/org/DiscountsPage'),
  inventory: () => import('@/pages/org/InventoryPage'),
  inventoryLedger: () => import('@/modules/inventory/ledger/InventoryLedgerPage'),
  warehouseSetup: () => import('@/modules/warehouse/setup/WarehouseSetupPage'),
  hrSetup: () => import('@/modules/hr/setup/HrSetupPage'),
  hrAllowanceTypes: () => import('@/modules/hr/allowances/HrAllowanceTypesPage'),
  hrMasterDataSettings: () => import('@/modules/settings/HrMasterDataSettingsPage'),
  hrOrg: () => import('@/modules/hr/org/HrOrgStructurePage'),
  hrAdmin: () => import('@/modules/hr/admin/HrAdminConfigPage'),
  financeSetup: () => import('@/modules/finance/setup/FinanceSetupPage'),
  financeTaxes: () => import('@/modules/finance/taxes/FinanceTaxesPage'),
  financeRules: () => import('@/modules/finance/administration/FinanceBusinessRulesPage'),
  financeAudit: () => import('@/modules/finance/audit/FinanceAuditTrailPage'),
  financeWorkflows: () => import('@/modules/finance/workflows/FinanceWorkflowsPage'),
  financeLedger: () => import('@/modules/finance/ledger/FinanceLedgerPage'),
  financeControl: () => import('@/modules/finance/workspaces/FinanceWorkspacePages'),
  financeHealth: () => import('@/modules/finance/administration/FinanceHealthCenterPage'),
  financeClose: () => import('@/modules/finance/administration/FinanceCloseCenterPage'),
  financeAutomation: () => import('@/modules/finance/automation/FinanceAutomationPage'),
  financeIntegrations: () => import('@/modules/finance/integrations/FinanceIntegrationsPage'),
  financeAccounting: () => import('@/modules/finance/accounting/FinanceAccountingPage'),
  crmSetup: () => import('@/modules/crm/setup/CrmSetupPage'),
  crmContacts: () => import('@/modules/crm/contacts/CrmContactsPage'),
  crmCompanies: () => import('@/modules/crm/companies/CrmCompaniesPage'),
  customers: () => import('@/pages/org/CustomersPage'),
  leadGenSources: () => import('@/modules/leadgen/LeadGenSourcesPage'),
  marketingTemplates: () => import('@/modules/marketing/MarketingTemplatesPage'),
  marketingSegments: () => import('@/modules/marketing/MarketingSegmentsPage'),
  marketingSuppression: () => import('@/modules/marketing/MarketingSuppressionPage'),
  marketingWebhooks: () => import('@/modules/marketing/MarketingEspWebhooksPage'),
  marketingAcquisition: () => import('@/modules/marketing/MarketingAcquisitionBuildersPage'),
  projectsSetup: () => import('@/modules/projects/setup/ProjectsSetupPage'),
  projectsBoards: () => import('@/modules/projects/boards/ProjectsBoardsPage'),
  projectsIssues: () => import('@/modules/projects/issues/ProjectsIssuesPage'),
  projectsResources: () => import('@/modules/projects/resources/ProjectsResourcesPage'),
  projectsAutomation: () => import('@/modules/projects/automation/ProjectsAutomationPage'),
  deskSetup: () => import('@/modules/desk/setup/DeskSetupPage'),
  deskWorkflow: () => import('@/modules/desk/setup/DeskWorkflowDesignerPage'),
  deskCannedResponses: () => import('@/modules/desk/canned/DeskCannedResponsesPage'),
  deskKnowledgeBase: () => import('@/modules/desk/knowledge/DeskKnowledgeBasePage'),
  deskBoards: () => import('@/modules/desk/boards/DeskBoardsPage'),
  approvalsBuilder: () => import('@/modules/approvals/ApprovalBuilderPage'),
  approvalsMatrix: () => import('@/modules/approvals/ApprovalMatrixPage'),
  approvalDelegations: () => import('@/modules/approvals/ApprovalDelegationsPage'),
  notificationsCenter: () => import('@/pages/work/NotificationsCenterPage'),
  socialConnect: () => import('@/modules/social/connection/SocialConnectionCenterPage'),
  socialSettings: () => import('@/modules/social/settings/SocialSettingsHubPage'),
  socialWebhooks: () => import('@/modules/social/webhooks/SocialWebhooksPage'),
  whatsappSettings: () => import('@/modules/social/whatsapp/settings/WhatsAppSettingsPage'),
  translations: () => import('@/modules/settings/localization/TranslationsPage'),
};

function warm(key: string, load: () => Promise<unknown>): void {
  if (prefetched.has(key)) return;
  prefetched.add(key);
  void load().catch(() => {
    prefetched.delete(key);
  });
}

/** Prefetch the lazy chunk for a settings master route (safe to call repeatedly). */
export function prefetchSettingsMaster(to: string): void {
  const pathname = to.split('?')[0].replace(/\/+$/, '') || '/';
  if (!pathname.startsWith('/settings')) return;

  const master = getSettingsMasterByPath(pathname) ?? SETTINGS_MASTER_BY_ROUTE.get(pathname);
  if (!master || master.componentKey === 'genericMasterPlaceholder' || master.componentKey === 'moduleSubscription') {
    return;
  }

  const load = COMPONENT_IMPORTS[master.componentKey];
  if (load) {
    warm(`component:${master.componentKey}`, load);
  }

  // HR master-data page statically split into kind-specific chunks
  if (master.componentKey === 'hrMasterDataSettings') {
    if (master.key === 'holiday-calendar') {
      warm('hr:holidays', () => import('@/modules/hr/holidays/HrHolidaysPage'));
    } else if (master.key === 'biometric-devices') {
      warm('hr:devices', () => import('@/modules/hr/devices/HrDevicesPage'));
    } else {
      warm('hr:master-data', () => import('@/modules/hr/master/HrMasterDataPage'));
    }
  }
}

/** Warm a few common masters after Settings hub opens (idle). */
export function prefetchSettingsHubWarmup(routes: string[]): void {
  const run = () => {
    for (const route of routes.slice(0, 8)) {
      prefetchSettingsMaster(route);
    }
  };
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => run(), { timeout: 1500 });
  } else {
    setTimeout(run, 200);
  }
}
